/**
 * SalesPipeline.tsx — Sales / Pipeline: the BOARD (S-Tier slice 3; redesign
 * slice 4, 2026-09-27, ADOPTION.md §16 — the calm-minimal Pipeline board).
 *
 * Visual authority since slice 4: docs/design/calm-minimal/Pipeline.dc.html
 * (the boards' lane geometry, sentence-case lane headers, the title row's
 * four figures, the Owner / Close date / Sort chips, the attention chip, the
 * "13d in stage" card line, the calendar footer) read into the board contract
 * through pipeline.css's `.k-pipeline--calm` block; behind it pipeline.css +
 * PIPELINE.md (the slice-0b addendum, ADOPTION.md §6).
 * Slice 4 decisions, each on a read path or omitted: "Nd in stage" is the
 * 0097 stage stamp the list already carries (daysInStage) — NOT a missing
 * field; the Board / List toggle is OMITTED (no list view exists; a dead
 * toggle is an inventory DEAD); the Lost lane STAYS (ruled: a drop target the
 * close ceremony uses); the all-time won figure is the server's closed_won
 * stage total on /v1/sales/stats; the chips filter and sort the rows the board
 * displays; the New Deal button lives in the shell's primary-action slot (2b
 * (4)) and opens the same drawer; the won lane shows every card (the boards'
 * "Show 3 more" would hide three affordances — no functionality lost is the
 * measured gate); the stage palette stays the app's (row 65).
 * Behaviour is the shipped board's, unchanged: native HTML5 drag-drop with
 * optimistic move + revert, the close ceremony as the ONLY path into closed
 * stages (drop on a closed lane or the card kebab both open CloseDealModal),
 * click-to-open Deal Detail, search filtering, per-stage create presets.
 *
 * VIEWPORT-OWNING (§6.5): this page renders inside the shell's viewportOwning
 * main (Dashboard.tsx sets it only while the BOARD is the rendered page) —
 * main stops scrolling/padding and .k-pipeline fills it; the board scrolls
 * horizontally, each lane vertically, the page never. The min-height: 0 on
 * .k-pipeline/.k-board/.k-stage/.k-lane is load-bearing (§6.5.3).
 *
 * Header figures (§6.6 data honesty): all four ride /v1/sales/reports — the
 * server's decided KPI formulas over the FULL deal set (never a derivation
 * over the limit-200 list). Since register row 84 (2026-10-02) they come from
 * the read SHARED with the sidebar's count (salesFigures.ts): a successful
 * close or create re-reads them silently, the last figures staying on screen
 * until the new ones land; an open-to-open move re-reads nothing, because it
 * changes none of them. "avg cycle" deliberately replaces PIPELINE.md's
 * "median cycle": no median exists anywhere on the read path, and the label
 * says what the number is. Lane values/shares/bars are computed from the
 * rows the board itself displays. Stage dots/bars bind to the app's pinned
 * per-stage palette (tokens stageDot) — NOT PIPELINE.md's colour table,
 * which would desync the board from every other Sales surface and map two
 * stages to one hue (recorded in ADOPTION.md §8).
 *
 * The per-deal status capsule PIPELINE.md shows (DISCOVERY/CHASE/…) has no
 * backing field and is OMITTED; the footer's right slot carries the honest
 * occupants instead — the WON/LOST outcome badge on closed cards, the
 * staleness badge on stale open ones.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { MoreHorizontal, Search, Calendar, AlertCircle } from 'lucide-react'
import { LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesDeal, DealStage, SalesContact } from '../../types'
import { useAsync, fetchDeals, fetchContacts, fetchContact, fetchAccount, fetchDealCard, fetchDealPreview, createDeal, mapDeal, moveDealStage, STAGE_TO_SLUG, cachedMeIdentity, fmtDate, type SalesAccount360 } from '../../api'
import type { SalesDealPreview } from '../../../../api/types'
import { loadSalesFigures, refreshSalesFigures, useSalesFigures } from '../../salesFigures'
import { CurrencySelect } from '../../CurrencySelect'
import { clearDraft, draftKey, readDraft, writeDraft } from '../../../../lib/draftStorage'
import { currencySymbol } from '../../../../lib/money'
import { OwnerSelect } from './OwnerSelect'
import { AccountPicker } from './AccountPicker'
import { linkedContactsSentence } from './dealCreateLinks'
import { SOURCE_OPTIONS } from './vocab'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { useLogActivity } from './logActivityShared'
import { accent, stageDot, success, danger, warning } from '../../../../theme/tokens'
import { SideSheet, SheetGroup, SheetRow, FieldError, Segmented } from './SideSheet'
import { newDealCreatable, withSeparators, newDealSummary, draftFresh } from './newDealSheet'
import { closePicks, closeWarning, defaultClose } from './newDealCloseDate'
import { DEAL_PREVIEW_DEBOUNCE_MS, dealPreviewLines, dealPreviewQuery } from './dealPreview'
import { autofillField, hasWonDeal, mostRecentOpen, pickContact, pickOwner, titleChips } from './newDealAutofill'
import { NewContactDrawer } from './SalesContacts'
import { DealHealthBadge } from './DealHealthBadge'
import { parsePoundsToPence } from '../../../../lib/money'
import { CloseDealModal } from './CloseDealModal'
import { dropAction, isClosedStage, type CloseOutcome } from './closeFlow'
import {
  laneLabel, daysLabel, unassigned, ownerOptions, closeState, applyBoardFilters, sortDeals, attentionCount, attentionLabel, footerBadge, ageStale,
  laneFigures, shareLabel, headFigures, CLOSE_FILTERS, SORT_KEYS, type CloseFilter, type SortKey,
} from './pipelineView'

// The six mockup columns, verbatim (design note §A.2) — including Closed
// Lost, which the mockup has and the old board omitted.
const STAGES: DealStage[] = [
  'New Lead',
  'Qualified',
  'Proposal Sent',
  'Negotiation',
  'Closed Won',
  'Closed Lost',
]

// The stages a deal can be BORN into. Closed stages are reached only through
// the close ceremony (design note §C / decision #7: the server refuses closed
// stages on the bare stage route because a close carries required data and a
// Finance side effect) — a create drawer offering them would mint deals that
// bypassed it.
const OPEN_STAGES: DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']

const PRIORITY_OPTIONS = [
  { value: '',       label: 'None' },
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
] as const

const STAGE_DOT: Record<DealStage, string> = stageDot

// Lane-header + card money: compact per PIPELINE.md's derived-value rules
// (£1.2M / £980k / £0 — full precision belongs to Deal Detail only). Same
// shape as the SalesDashboard/SalesReports private copies. Per-row calls
// pass the deal's ORIGINAL currency (0111: non-GBP deals exist once
// captured); lane/board figures stay £ — they sum the GBP-comparable leg.
export function fmtCompact(pence: number, currency = 'GBP') {
  // SB-10: symbol derivation via the shared rule; a CODE keeps its
  // trailing space ("CAD 1.2M"), symbols stay unspaced — byte-identical
  // to the retired inline derivation. Pence + compaction untouched.
  const s = currencySymbol(currency)
  const sym = s === currency ? `${s} ` : s
  const n = pence / 100
  // Boundary (register row 67): a value that would ROUND to 1000 in the smaller
  // unit rolls to the next one — £999,500 and up prints in M (never "£1000k"),
  // £999.50 and up in k (never "£1000"). The same two thresholds in every copy.
  if (n >= 999_500) return `${sym}${(n / 1_000_000).toFixed(1)}M`
  if (n >= 999.5) return `${sym}${(n / 1_000).toFixed(0)}k`
  return `${sym}${n.toFixed(0)}`
}

function SummaryItem({ value, label }: { value: string; label: string }) {
  return (
    <span className="k-summary-item">
      <span className="k-summary-value text-ink-primary">{value}</span>
      <span className="k-summary-label">{label}</span>
    </span>
  )
}

// ── New Deal sheet ────────────────────────────────────────────────────────────
// Exported: the Dashboard's New Deal button reuses this sheet (P10 parity —
// the NewContactDrawer-from-SalesContacts precedent; never duplicate it).
//
// Slice 1 of the New Deal sheet (docs/design/calm-minimal/specs/
// klaxiora-new-deal-spec.md §3, as corrected by ADOPTION.md §18; RULED
// 2026-10-06): the sheet's semantics and layout, with the EXISTING fields
// reordered — company first — into the spec's grouped inset list. No new
// behaviour reads or writes anything the drawer did not already: the
// duplicate card, autofill, title chips, close-date quick picks and the live
// summary are later slices.
//
//   - A floating sheet over a scrim WITHOUT blur. role="dialog",
//     aria-modal, labelled by its title; Tab is trapped inside it and focus
//     returns to the opener on close (lib/focusTrap.ts). Focus starts in
//     Company.
//   - The scrim and Esc close it only while it is untouched (draftTouched —
//     the SB-8 rule for "has content"); otherwise they ask "Discard this
//     deal?". The X and Cancel are deliberate closes and do not ask: the SB-8
//     draft still flushes on unmount, so nothing typed is lost by them.
//     "Discard deal" in the confirm is an explicit throw-away and clears the
//     draft too, the same as the draft notice's own Discard.
//   - Tokens are the existing ones, mapped by role (no token added): the
//     sheet ground is the page ground (surface.app — the spec's sheet grey), the
//     group cards are surface.card on the tile radius (14), rows divide on
//     the hairline (edge.row), the sheet takes the card radius (20 — the spec
//     asks 22; the system has no 22) and the ONE system shadow (shadow.card,
//     whose 1px ring is the spec's "hairline ring"; the spec's long drop
//     shadow has no token).
//
// Create is enabled only when company, title and the value FIELD are
// non-empty — "0" is a value. The server's rules are unchanged: it still
// accepts a deal with no value, and an absent priority or source.
//
// Slice 3 (RULED 2026-10-06) finishes the sheet: close-date quick picks and
// a default (newDealCloseDate.ts, the clock read once per mount), the two
// warnings, the Unassigned hint, the footer's live summary, Create another,
// ⌘↵ / Ctrl+↵, inline validation, the success toast's View link, and the
// draft's ten-minute life behind a "Restore draft?" prompt. Each warning's
// sentence is what the server does, verified in its code:
//   - no close date: /forecast's Commit due, Best case stretch, Locked,
//     Stretch and next-period figures all need a close date in a window, so
//     an undated deal counts toward NO period (it is listed as undated, and
//     still counts in the by-category roll-up, which is not period-bound) —
//     the spec's "left out of forecasts" overstates it;
//   - Unassigned: a rep's attainment counts only won deals carrying their
//     employee id, and no team target exists — the spec's sentence is true.
// The owner default stays Unassigned: neither /v1/me nor any read links the
// signed-in user to an employee, so "the current user, if an employee" has
// nothing to resolve against.

interface NewDealDrawerProps {
  initialStage: DealStage
  onClose: () => void
  onCreated: (deal: SalesDeal) => void
  /** Slice 2: the duplicate card's "Open existing deal" — the host opens
   *  that deal's detail; the sheet closes (its SB-8 draft flushes as on any
   *  close). Absent = the button is not offered. */
  onOpenDeal?: (dealId: string) => void
}

// Slice 2 (RULED): "It's a separate deal" dismisses the duplicate card FOR
// THE SESSION — the account ids, held for the page's life, never stored.
const DISMISSED_DUPLICATES = new Set<string>()

// The autofill tag under a field: says WHY the value is there, and goes the
// moment the user edits that field.
function AutofillTag({ text }: { text: string | null }) {
  if (!text) return null
  return <div className="text-12 text-ink-muted mt-1.5">✦ {text}</div>
}

// ── SB-8: local draft — accident recovery only. LOCAL storage, THIS form
// only; no server state, no draft stage, no pipeline visibility. ────────────
interface NewDealDraft {
  title: string; company: string; value: string; currency: string
  stage: DealStage; expectedClose: string; owner: string; source: string
  priority: '' | 'low' | 'medium' | 'high'; contactId: string; notes: string
  /** Slice A (A2.3): the picked account; '' when company is free text. */
  accountId: string
  /** Slice 3 (S10): when it was written (ms). A draft is offered for ten
   *  minutes after this, and not after (newDealSheet.draftFresh). */
  savedAt?: number
}

const NEW_DEAL_DRAFT_FORM = 'new-deal'

// "Touched" = any CONTENT field carries user input. Stage is EXCLUDED: it is
// prop-seeded by whichever add-button opened the drawer and costs one click
// to re-pick — a stage-only interaction is not worth a draft, and this rule
// is what stops an untouched stray open writing an empty draft over a real
// one. The same rule decides whether closing must ask first. Slice 3: the
// close date counts only when it differs from the sheet's own default.
function draftTouched(d: NewDealDraft, defaultCloseIso: string): boolean {
  return d.title !== '' || d.company !== '' || d.value !== '' || d.currency !== 'GBP' ||
    d.expectedClose !== defaultCloseIso || d.owner !== '' || d.source !== '' || d.priority !== '' ||
    d.contactId !== '' || d.notes !== ''
}

const STAGE_OPTIONS = OPEN_STAGES.map(s => ({ value: s, label: laneLabel(s) }))

// The contact select's sentinel for "+ New contact" — never a contact id.
const NEW_CONTACT = '__new-contact__'

// Slice 3 (F5): the required fields in their DOM order, each with its input
// id — the first invalid one is scrolled into view on a submit attempt.
type RequiredField = 'company' | 'title' | 'value'
const REQUIRED_FIELDS: readonly RequiredField[] = ['company', 'title', 'value']
const FIELD_INPUT_ID: Record<RequiredField, string> = {
  company: 'new-deal-company', title: 'new-deal-title-input', value: 'new-deal-value',
}
const VALUE_FORMAT_ERROR = 'Enter a positive amount with up to 2 decimals, e.g. 18,000 or 9500.50'

export function NewDealDrawer({ initialStage, onClose, onCreated, onOpenDeal }: NewDealDrawerProps) {
  // SB-8 identity: the CACHED /v1/me (session truth — never mc:tenantId,
  // which defaults to "dev-tenant" and disagrees in WorkOS mode). Null until
  // /me resolves or if it failed — then drafts are INERT for this mount (no
  // restore, no autosave): no draft is better than one under the wrong key.
  // In practice /me resolves at dashboard boot, long before any drawer can
  // open, so the window is unobservable.
  const [dKey] = useState(() => {
    const id = cachedMeIdentity()
    return id ? draftKey(NEW_DEAL_DRAFT_FORM, id.tenantId, id.subject) : null
  })
  // The clock, read ONCE per mount (a render must not call it): the close
  // date's picks, default and warnings all hang off this one reading.
  const [now] = useState(() => new Date())
  const [defaultCloseIso] = useState(() => defaultClose(now))
  // Slice 3 (S10): a stored draft is OFFERED, never silently restored — and
  // only while it is under ten minutes old. A LAZY INITIAL read, so the
  // prompt is there on the first paint. A stale draft is cleared below.
  const [stored] = useState(() => (dKey ? readDraft<NewDealDraft>(dKey) : null))
  const [pending, setPending] = useState(() => (stored && draftFresh(stored.savedAt, now.getTime()) ? stored : null))
  useEffect(() => {
    if (dKey && stored && !draftFresh(stored.savedAt, now.getTime())) clearDraft(dKey)
  }, [dKey, stored, now])

  const [title, setTitle]                 = useState('')
  const [company, setCompany]             = useState('')
  const [accountId, setAccountId]         = useState('')
  const [value, setValue]                 = useState('')
  // Increment-3 picker: deals default GBP; the server captures-or-refuses
  // a non-GBP value at create (the 2c fence).
  const [currency, setCurrency]           = useState('GBP')
  // The add-button's stage WINS over a restored draft: it is the user's
  // most recent explicit intent, and a draft overriding the lane they just
  // clicked would make that button look broken. The other fields restore.
  const [stage, setStage]                 = useState<DealStage>(initialStage)
  const [expectedClose, setExpectedClose] = useState(defaultCloseIso)
  const [owner, setOwner]                 = useState('')
  const [source, setSource]               = useState('')
  const [priority, setPriority]           = useState<'' | 'low' | 'medium' | 'high'>('')
  const [contactId, setContactId]         = useState('')
  const [notes, setNotes]                 = useState('')
  const [saving, setSaving]               = useState(false)
  // Slice 3: "Pick date" reveals the native date input; Create another.
  const [pickerOpen, setPickerOpen]       = useState(false)
  const [another, setAnother]             = useState(false)
  // F5: a field's error shows only after it was blurred, or after a submit
  // attempt — never while the user is still on their first pass.
  const [blurred, setBlurred]             = useState<Partial<Record<RequiredField, boolean>>>({})
  const [attempted, setAttempted]         = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)
  // Live mirror of the submit gate: parsePoundsToPence enforces exactly what
  // the value column takes (NUMERIC(12,2): numeric, non-negative, ≤2
  // decimals, ≤10 integer digits) — the inline error and the save toast are
  // the SAME rule, so nothing typable posts as nonsense.
  const valueInvalid = value.trim() !== '' && parsePoundsToPence(value) === null
  const creatable = newDealCreatable(company, title, value)
  const errors: Record<RequiredField, string | null> = {
    company: company.trim() === '' ? 'Company is required' : null,
    title: title.trim() === '' ? 'Deal title is required' : null,
    value: value.trim() === '' ? 'Value is required' : valueInvalid ? VALUE_FORMAT_ERROR : null,
  }
  const shownError = (f: RequiredField) => (blurred[f] || attempted ? errors[f] : null)
  // A blur counts only when focus moves to another place in the sheet, or to
  // nowhere. Focus LEAVING the dialog is never the user finishing a field: it
  // is the opener being refocused (the trap's cleanup — which React's dev
  // StrictMode also replays on mount, measured in the visual suite as an
  // error under a Company nobody had touched) or the sheet closing.
  const blurFrom = (f: RequiredField) => (e: React.FocusEvent) => {
    const to = e.relatedTarget
    const sheet = e.currentTarget.closest('[role="dialog"]')
    if (to instanceof Node && (e.currentTarget.contains(to) || !sheet?.contains(to))) return
    setBlurred(b => (b[f] ? b : { ...b, [f]: true }))
  }
  const contacts = useAsync(fetchContacts, [])
  const [newContactOpen, setNewContactOpen] = useState(false)

  const latest = useRef<NewDealDraft>({ title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes })
  latest.current = { title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes }

  // ── Slice 2: what the picked account already knows ───────────────────────
  // ONE read per pick. A failed or slow read shows nothing and blocks
  // nothing: no spinner, no error, the form works exactly as unlinked.
  const [acct, setAcct] = useState<{ id: string; data: SalesAccount360 } | null>(null)
  const [ownerTag, setOwnerTag] = useState<string | null>(null)
  const [contactTag, setContactTag] = useState<string | null>(null)
  const ownerEdited = useRef(false)
  const contactEdited = useRef(false)
  const [people, setPeople] = useState<{ id: string; fullName: string }[] | null>(null)
  const onPeople = useCallback((p: { id: string; fullName: string }[]) => setPeople(p), [])
  const peopleIds = people ? people.map(p => p.id) : null
  // Bumped by a Create another so the account is read again: the duplicate
  // card must name the deal just created, never the stale read.
  const [acctReads, setAcctReads] = useState(0)
  // The account whose duplicate card was dismissed in THIS sheet (the
  // session-wide Set below carries it across sheets).
  const [dupDismissedFor, setDupDismissedFor] = useState('')
  const [createdContacts, setCreatedContacts] = useState<SalesContact[]>([])

  useEffect(() => {
    if (!accountId) return
    let live = true
    fetchAccount(accountId)
      .then(data => { if (live) setAcct({ id: accountId, data }) })
      .catch(() => { /* a failed read shows nothing (RULED) */ })
    return () => { live = false }
  }, [accountId, acctReads])
  const detail = acct && acct.id === accountId ? acct.data : null

  // ── Slice 4b: the preview (GET /v1/sales/deal-preview, PR 641) ─────────────
  // Asked DEAL_PREVIEW_DEBOUNCE_MS after the last edit, and only for a value
  // that parses. Stale-safe twice over: a reply whose draft has since changed
  // is dropped (live), and a preview shows only while its own query is the
  // current one (key) — so an edit hides the old lines at once rather than
  // leaving figures the new value contradicts. A failure or a slow reply
  // shows nothing and touches neither Create nor any validation (RULED).
  const previewQ = dealPreviewQuery(value, currency, STAGE_TO_SLUG[stage], expectedClose)
  const previewKey = previewQ ? JSON.stringify(previewQ) : ''
  const [preview, setPreview] = useState<{ key: string; data: SalesDealPreview } | null>(null)
  useEffect(() => {
    if (!previewKey) return
    let live = true
    const t = setTimeout(() => {
      fetchDealPreview(JSON.parse(previewKey))
        .then(data => { if (live) setPreview({ key: previewKey, data }) })
        .catch(() => { /* a failed read shows nothing (RULED) */ })
    }, DEAL_PREVIEW_DEBOUNCE_MS)
    return () => { live = false; clearTimeout(t) }
  }, [previewKey])
  const lines = dealPreviewLines(preview && preview.key === previewKey ? preview.data : null, fmtCompact)

  // Autofill, field by field, through the pure rule (newDealAutofill.ts):
  // an edited field is never touched; an empty or still-tagged one takes the
  // account's pick. Unlinking the account (detail null) clears only what is
  // still tagged. The owner waits for the select's own people list, so it
  // never sets an id the select cannot show.
  useEffect(() => {
    const cur = latest.current
    const c = autofillField(
      { value: cur.contactId, tag: contactTag, edited: contactEdited.current },
      detail ? (p => p && { value: p.contactId, tag: p.tag })(pickContact(detail.contacts)) : null,
    )
    if (c) { setContactId(c.value); setContactTag(c.tag) }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per account read; the tag is read, not a trigger
  }, [detail])
  useEffect(() => {
    if (detail && peopleIds === null) return
    const cur = latest.current
    const p = detail ? pickOwner(detail.openDeals) : null
    const offered = p && peopleIds?.includes(p.employeeId) ? { value: p.employeeId, tag: p.tag } : null
    const o = autofillField({ value: cur.owner, tag: ownerTag, edited: ownerEdited.current }, offered)
    if (o) { setOwner(o.value); setOwnerTag(o.tag) }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per account read and people load
  }, [detail, people])

  const recentOpen = detail ? mostRecentOpen(detail.openDeals) : null
  const showDuplicate = recentOpen !== null && dupDismissedFor !== accountId && !DISMISSED_DUPLICATES.has(accountId)
  const chips = titleChips(company, detail ? hasWonDeal(detail.closedDeals) : false)
  // The contact list: the account's own contacts once its read lands (plus a
  // contact made here, and a restored pick from elsewhere, so the select can
  // always SHOW its value); every contact otherwise.
  const contactOptions: SalesContact[] = detail
    ? [...detail.contacts, ...createdContacts.filter(c => !detail.contacts.some(d => d.id === c.id)),
       ...(contactId && !detail.contacts.some(d => d.id === contactId) && !createdContacts.some(d => d.id === contactId)
         ? (contacts.data ?? []).filter(c => c.id === contactId) : [])]
    : [...createdContacts, ...(contacts.data ?? []).filter(c => !createdContacts.some(d => d.id === c.id))]


  // ── SB-8 autosave ──────────────────────────────────────────────────────────
  // One debounced effect over the assembled tuple (never per-keystroke), and
  // a FLUSH on unmount — closing the drawer IS the accident this recovers,
  // so the last keystrokes must not lose a debounce race. draftDone stops
  // the flush from resurrecting a draft the successful submit just cleared.
  //
  // Slice 3: every write carries savedAt, and NOTHING is written while the
  // "Restore draft?" prompt is unanswered — the stored draft is the one
  // being offered, and typing over it must not replace it unasked.
  const draftDone = useRef(false)
  const awaiting = useRef(pending !== null)
  useEffect(() => { awaiting.current = pending !== null }, [pending])
  useEffect(() => {
    if (!dKey || pending || !draftTouched(latest.current, defaultCloseIso)) return
    const t = setTimeout(() => { if (!draftDone.current) writeDraft(dKey, { ...latest.current, savedAt: Date.now() }) }, 400)
    return () => clearTimeout(t)
  }, [dKey, pending, defaultCloseIso, title, company, accountId, value, currency, stage, expectedClose, owner, source, priority, contactId, notes])
  useEffect(() => {
    if (!dKey) return
    return () => {
      if (!draftDone.current && !awaiting.current && draftTouched(latest.current, defaultCloseIso)) {
        writeDraft(dKey, { ...latest.current, savedAt: Date.now() })
      }
    }
  }, [dKey, defaultCloseIso])

  // The prompt's two answers.
  function restoreDraft() {
    if (!pending) return
    setTitle(pending.title ?? ''); setCompany(pending.company ?? ''); setAccountId(pending.accountId ?? '')
    setValue(pending.value ?? ''); setCurrency(pending.currency ?? 'GBP')
    setExpectedClose(pending.expectedClose ?? defaultCloseIso); setOwner(pending.owner ?? '')
    setSource(pending.source ?? ''); setPriority(pending.priority ?? ''); setContactId(pending.contactId ?? '')
    setNotes(pending.notes ?? '')
    setPending(null)
  }
  function discardDraft() {
    if (dKey) clearDraft(dKey)
    setPending(null)
  }

  function discardAndClose() {
    if (dKey) clearDraft(dKey)
    draftDone.current = true
    onClose()
  }

  async function handleSave() {
    // F5: a submit attempt shows every field's error and scrolls the FIRST
    // invalid field into view; nothing is sent. Server errors stay toasts.
    setAttempted(true)
    const firstInvalid = REQUIRED_FIELDS.find(f => errors[f] !== null)
    if (firstInvalid) {
      document.getElementById(FIELD_INPUT_ID[firstInvalid])?.scrollIntoView?.({ block: 'center' })
      return
    }
    // Money crosses the boundary as integer pence (D9): parse the pounds text
    // to pence, put pence/100 on the wire's pounds field — the raw input
    // string never becomes a float. Non-null here: the value passed above.
    const pence = parsePoundsToPence(value)
    if (pence === null) return
    setSaving(true)
    try {
      const created = await createDeal({
        title: title.trim(),
        company: company.trim(),
        // Slice A: the picker's link travels with the create; the module
        // validates it in-tenant.
        ...(accountId ? { accountId } : {}),
        stage: STAGE_TO_SLUG[stage],
        value: pence / 100,
        // GBP is the server default; sent only when the picker moved off it.
        ...(currency !== 'GBP' ? { currency } : {}),
        ...(contactId ? { contactId } : {}),
        // Go *time.Time is strict RFC3339 — never a bare date.
        ...(expectedClose ? { expectedClose: `${expectedClose}T00:00:00Z` } : {}),
        // E360-2: OwnerSelect emits the employee ID; the server refuses a name.
        ...(owner.trim() ? { assignedEmployeeId: owner.trim() } : {}),
        ...(source.trim() ? { source: source.trim() } : {}),
        ...(priority ? { priority } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      })
      // SB-8: clear the draft on SUCCESS only. The catch path leaves the
      // form AND the draft intact — that is exactly the data the user most
      // wants back after a failed request.
      if (dKey) clearDraft(dKey)
      draftDone.current = true
      // Row 84: a create moves the open figures — re-read them for the board
      // header and the sidebar count, wherever this drawer is mounted.
      void refreshSalesFigures()
      onCreated(created)
      // F6: "Deal created · {title} · View" — View opens the deal where the
      // host can (Pipeline and Home pass onOpenDeal); no opener, no link.
      // §25 L-11a: the fan-out is SAID, with names — never a silent write.
      const linked = linkedContactsSentence(created.linkedContacts, created.company)
      const createdId = created.id
      toast.success(`Deal created · ${created.name || title.trim()}`, {
        ...(linked ? { description: linked } : {}),
        ...(onOpenDeal ? { action: { label: 'View', onClick: () => onOpenDeal(createdId) } } : {}),
      })
      if (another) {
        // F4: keep company (and its account), owner, stage and close date;
        // clear title, value and notes; the title takes focus. Fields the
        // ruling names neither way keep their values.
        setTitle(''); setValue(''); setNotes('')
        setBlurred({}); setAttempted(false)
        draftDone.current = false
        if (accountId) setAcctReads(n => n + 1)
        titleRef.current?.focus()
      } else {
        onClose()
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create deal'))
    } finally {
      setSaving(false)
    }
  }

  const picks = closePicks(now)
  const closeWarn = closeWarning(expectedClose, now)
  // The owner's NAME for the summary: '' = unassigned; null = an owner whose
  // name the loaded people list cannot give (the segment is left out rather
  // than claim no owner).
  const ownerName = owner === '' ? '' : (people?.find(p => p.id === owner)?.fullName ?? null)
  const summary = newDealSummary({ stage: laneLabel(stage), closeIso: expectedClose, ownerName })

  const inputCls = "w-full px-3 py-2 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"
  const chipCls = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-13 font-semibold border transition-all ${active ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control text-ink-secondary hover:border-ink-disabled'}`

  return (
    <SideSheet titleId="new-deal-title" title="New deal"
      subtitle="Start with the company. We'll fill in what we already know."
      headerExtra={pending && (
        <div role="group" aria-labelledby="new-deal-restore-label" className="flex items-center gap-3 mt-2">
          <span id="new-deal-restore-label" className="text-13 font-semibold text-ink-primary">Restore draft?</span>
          <button type="button" onClick={restoreDraft} className="k-btn-secondary">Restore</button>
          <button type="button" onClick={discardDraft} className="k-btn-secondary">Discard</button>
        </div>
      )}
      footer={(
        // The live summary (F1), Create another (F4), the actions.
        <>
          <p aria-live="polite" className="text-13 text-ink-secondary">✦ {summary}</p>
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-13 text-ink-body cursor-pointer">
              <input type="checkbox" checked={another} onChange={e => setAnother(e.target.checked)} />
              Create another
            </label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} className="k-btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving || !creatable}
                aria-keyshortcuts="Meta+Enter Control+Enter"
                className="k-btn k-btn--lg disabled:opacity-60">
                {saving ? 'Creating…' : 'Create deal'}
                {!saving && <span aria-hidden="true" className="ml-2 opacity-70">⌘↵</span>}
              </button>
            </div>
          </div>
        </>
      )}
      onClose={onClose}
      isDirty={() => draftTouched(latest.current, defaultCloseIso)}
      suspended={newContactOpen}
      discardTitle="Discard this deal?" discardBody="What you've entered will be lost."
      discardLabel="Discard deal" onDiscard={discardAndClose}
      onKeyDown={e => {
        // F3: ⌘↵ / Ctrl+↵ submits — only when Create is enabled, and never
        // from the New Contact drawer (its events bubble here through the
        // portal) or the discard confirm (the frame does not call this then).
        if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return
        if (newContactOpen) return
        e.preventDefault()
        if (creatable && !saving) void handleSave()
      }}>

      <SheetGroup caption="Company and deal">
        <SheetRow label="Company *" htmlFor="new-deal-company">
          {/* Slice A (A2.3): the account picker; free text stays legal,
              a pick also links. First field, and where focus starts. */}
          {/* The picker's own onBlur carries no event; its wrapper's does
              (focusout bubbles), and a move into its own list is not a
              blur of the field. */}
          <div onBlur={blurFrom('company')}>
            <AccountPicker id="new-deal-company" value={{ accountId, company }}
              onChange={p => { setAccountId(p.accountId); setCompany(p.company) }}
              placeholder="Orbital Systems" autoFocus className={inputCls} />
          </div>
          <FieldError id="new-deal-company-error" text={shownError('company')} />
          {showDuplicate && recentOpen && (
            <div role="status" className="mt-3 rounded-tile px-3 py-3 text-13"
              style={{ background: warning.bg, color: warning.text }}>
              <div>
                This account already has an open deal: {recentOpen.deal.name} · {fmtCompact(recentOpen.deal.amount, recentOpen.deal.currency)} · {laneLabel(recentOpen.deal.stage)} · {recentOpen.deal.owner.trim() && recentOpen.deal.owner !== '—' ? recentOpen.deal.owner : 'Unassigned'}
              </div>
              {recentOpen.more > 0 && (
                <div className="mt-1">and {recentOpen.more} more open {recentOpen.more === 1 ? 'deal' : 'deals'} at this account</div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {onOpenDeal && (
                  <button type="button" className="k-btn-secondary"
                    onClick={() => { onOpenDeal(recentOpen.deal.id); onClose() }}>
                    Open existing deal
                  </button>
                )}
                <button type="button" className="k-btn-secondary"
                  onClick={() => { DISMISSED_DUPLICATES.add(accountId); setDupDismissedFor(accountId) }}>
                  It's a separate deal
                </button>
              </div>
            </div>
          )}
        </SheetRow>
        <SheetRow label="Deal title *" htmlFor="new-deal-title-input">
          <input id="new-deal-title-input" ref={titleRef} type="text" value={title} onChange={e => setTitle(e.target.value)}
            onBlur={blurFrom('title')}
            aria-invalid={shownError('title') !== null}
            aria-describedby={shownError('title') ? 'new-deal-title-error' : undefined}
            placeholder="What is this deal for?" className={inputCls} />
          <FieldError id="new-deal-title-error" text={shownError('title')} />
          {chips.length > 0 && (
            <div role="group" aria-label="Title suggestions" className="flex flex-wrap gap-2 mt-2">
              {chips.map(c => (
                <button key={c} type="button" onClick={() => setTitle(c)} className={chipCls(false)}>{c}</button>
              ))}
            </div>
          )}
        </SheetRow>
      </SheetGroup>

      <SheetGroup caption="Value and stage">
        <SheetRow label="Value *" htmlFor="new-deal-value">
          {/* The currency control BESIDE the amount — no separate
              "Value (GBP)" label and dropdown pair. */}
          <div className="flex items-center gap-2">
            <CurrencySelect id="new-deal-currency" value={currency} onChange={setCurrency}
              className="flex-shrink-0 w-28 px-3 py-2 bg-surface-app border border-edge-control rounded-control text-13 font-semibold text-ink-body" />
            <input id="new-deal-value" type="text" inputMode="decimal" value={value}
              onChange={e => setValue(e.target.value)}
              onBlur={e => { setValue(v => withSeparators(v)); blurFrom('value')(e) }}
              placeholder="0" aria-invalid={shownError('value') !== null}
              aria-describedby={shownError('value') ? 'new-deal-value-error' : undefined}
              className="min-w-0 flex-1 px-3 py-1 bg-transparent border-0 text-24 font-semibold text-ink-primary placeholder:text-ink-faint" />
          </div>
          <FieldError id="new-deal-value-error" text={shownError('value')} />
          {lines.converted && <div className="text-12 text-ink-muted mt-1.5">{lines.converted}</div>}
          {lines.noRate && <div className="text-12 mt-1.5" style={{ color: warning.text }}>{lines.noRate}</div>}
          {lines.hint && <div className="text-12 mt-1.5" style={{ color: warning.text }}>{lines.hint}</div>}
        </SheetRow>
        <SheetRow label="Stage">
          <Segmented label="Stage" options={STAGE_OPTIONS} value={stage} onChange={setStage} />
          {lines.suggestion && <div className="text-12 text-ink-muted mt-1.5">{lines.suggestion}</div>}
        </SheetRow>
      </SheetGroup>

      <SheetGroup caption="Timing and people">
        <SheetRow label="Expected close" htmlFor={pickerOpen ? 'new-deal-close' : undefined}
          aside={<span className="text-14 font-semibold text-ink-primary" data-testid="new-deal-close-shown">
            {expectedClose ? fmtDate(`${expectedClose}T12:00:00Z`) : 'No date'}
          </span>}>
          {/* Slice 3 (T1–T4): the quick picks, then "Pick date" — the
              existing native date input, revealed — as the picker. */}
          <div role="group" aria-label="Close date quick picks" className="flex flex-wrap gap-2">
            {picks.map(p => {
              const active = expectedClose === p.iso
              return (
                <button key={p.key} type="button" aria-pressed={active} onClick={() => setExpectedClose(p.iso)}
                  className={chipCls(active)} style={active ? { background: accent.DEFAULT } : {}}>
                  {p.label}
                </button>
              )
            })}
            <button type="button" aria-pressed={pickerOpen} onClick={() => setPickerOpen(o => !o)} className={chipCls(false)}>
              Pick date
            </button>
          </div>
          {pickerOpen && (
            <input id="new-deal-close" type="date" value={expectedClose} onChange={e => setExpectedClose(e.target.value)}
              autoFocus className={`${inputCls} mt-2`} />
          )}
          {closeWarn === 'past' && (
            <div className="text-12 mt-1.5" style={{ color: danger.text }}>That date has passed</div>
          )}
          {closeWarn === 'missing' && (
            <div className="text-12 mt-1.5" style={{ color: warning.text }}>Deals without a close date count toward no forecast period</div>
          )}
        </SheetRow>
        <SheetRow label="Owner">
          {/* SB-2: picklist from employees — writes the same assigned_to
              TEXT the Reports GROUP BY reads (no divergence possible). */}
          <OwnerSelect value={owner} onPeople={onPeople}
            onChange={v => { ownerEdited.current = true; setOwnerTag(null); setOwner(v) }}
            className={`${inputCls} bg-surface-card`} />
          <AutofillTag text={ownerTag} />
          {owner === '' && (
            <div className="text-12 mt-1.5" style={{ color: warning.text }}>Unassigned deals count toward no one's target.</div>
          )}
        </SheetRow>
        <SheetRow label="Primary contact" htmlFor="new-deal-contact">
          <select id="new-deal-contact" value={contactId}
            onChange={e => {
              if (e.target.value === NEW_CONTACT) { setNewContactOpen(true); return }
              contactEdited.current = true; setContactTag(null); setContactId(e.target.value)
            }}
            disabled={!detail && !contacts.data} className={`${inputCls} bg-surface-card`}>
            <option value="">
              {!detail && contacts.loading ? 'Loading contacts…' : !detail && contacts.error ? 'Contacts unavailable' : 'No contact'}
            </option>
            {contactOptions.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.title ? ` · ${c.title}` : ''}{!detail && c.company ? ` — ${c.company}` : ''}</option>
            ))}
            <option value={NEW_CONTACT}>+ New contact</option>
          </select>
          <AutofillTag text={contactTag} />
        </SheetRow>
        <SheetRow label="Priority">
          <Segmented label="Priority" options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} />
        </SheetRow>
      </SheetGroup>

      <SheetGroup caption="More">
        <SheetRow label="Source">
          {/* SB-2: the SHIPPED slug vocabulary (server-validated since
              0107), single-select; clicking the chosen chip clears it. */}
          <div role="group" aria-label="Source" className="flex items-center gap-2 flex-wrap">
            {SOURCE_OPTIONS.map(o => {
              const active = source === o.value
              return (
                <button key={o.value} type="button" aria-pressed={active}
                  onClick={() => setSource(active ? '' : o.value)}
                  className={chipCls(active)}
                  style={active ? { background: accent.DEFAULT } : {}}>
                  {o.label}
                </button>
              )
            })}
          </div>
        </SheetRow>
        <SheetRow label="Notes" htmlFor="new-deal-notes">
          <textarea id="new-deal-notes" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Anything the team should know"
            rows={3}
            className="w-full px-0 py-1 bg-transparent border-0 text-13 text-ink-body placeholder:text-ink-faint resize-none" />
        </SheetRow>
      </SheetGroup>

    {newContactOpen && (
      <NewContactDrawer
        onClose={() => setNewContactOpen(false)}
        onCreated={c => {
          setCreatedContacts(prev => [c, ...prev])
          contactEdited.current = true; setContactTag(null); setContactId(c.id)
          setNewContactOpen(false)
        }} />
    )}
    </SideSheet>
  )
}

// ── Deal card (.k-deal) ───────────────────────────────────────────────────────

// Owner initials, .k-deal-owner treatment (the board's .k-avatar-on-panel2
// variant). Renders NOTHING for an unassigned deal — no fabricated owner.
function OwnerChip({ owner }: { owner: string }) {
  if (!owner.trim()) return null
  const initials = owner.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return <span className="k-deal-owner" title={owner}>{initials}</span>
}

function DealCard({ deal, entering = false, onEntered, onOpen, onDragStart, onDragEnd, onCloseAs, onMoveKey, onLogActivity }: {
  deal: SalesDeal
  /** Slice 3 (F7): just created from the sheet — the card animates in once
   *  (.k-deal--enter; none under prefers-reduced-motion, pipeline.css). */
  entering?: boolean
  onEntered?: (id: string) => void
  onOpen?: (id: string) => void
  onDragStart?: (id: string) => void
  onDragEnd?: () => void
  onCloseAs?: (deal: SalesDeal, outcome: CloseOutcome) => void
  /** Cmd/Ctrl + ←/→ (PIPELINE.md behaviour): move to the adjacent stage. */
  onMoveKey?: (deal: SalesDeal, dir: -1 | 1) => void
  /** Log Activity slice C1 (R5): the kebab's FIRST item, when a host exists. */
  onLogActivity?: (deal: SalesDeal) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const closed = isClosedStage(deal.stage)
  const lost = deal.stage === 'Closed Lost'
  // 2.4 (P2-12): the server's worst signal, or null for [] / null / absent —
  // slice 4: the worst of the signals OTHER than stage_stale, which the
  // "Nd in stage" line renders in place (pipelineView.footerBadge).
  const badge = footerBadge(deal.health)
  return (
    <article data-deal-card={deal.id}
      draggable={!closed}
      tabIndex={0}
      aria-label={`${deal.name}, ${deal.company}`}
      onDragStart={e => {
        onDragStart?.(deal.id)
        // Fade the SOURCE card only after the browser snapshots the drag
        // ghost — flagging it synchronously would fade the ghost too.
        const el = e.currentTarget
        requestAnimationFrame(() => el.setAttribute('data-dragging', 'true'))
      }}
      onDragEnd={e => {
        e.currentTarget.removeAttribute('data-dragging')
        onDragEnd?.()
      }}
      onClick={() => onOpen?.(deal.id)}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(deal.id) }
        if (!closed && (e.metaKey || e.ctrlKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          e.preventDefault()
          onMoveKey?.(deal, e.key === 'ArrowLeft' ? -1 : 1)
        }
      }}
      onAnimationEnd={e => { if (entering && e.target === e.currentTarget) onEntered?.(deal.id) }}
      className={`k-deal relative group ${entering ? 'k-deal--enter' : ''} ${closed ? 'cursor-pointer' : ''} ${lost ? 'opacity-60' : ''}`}>
      <div className="k-deal-top">
        <div className="flex flex-col gap-[3px] min-w-0">
          <span className="k-deal-name text-ink-primary">{deal.name}</span>
          <span className="k-deal-company k-truncate">{deal.company}</span>
          {lost && deal.lostReason && (
            <span className="text-11 text-ink-muted">{deal.lostReason}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Kebab = the non-drag close path (§5.B: HTML5 DnD has no touch
              support; this is also the mockup's Close-button route into
              confirmCloseDeal). Hidden on closed cards — no reopen path. */}
          {!closed && (
            <button aria-label={`Deal options for ${deal.name}`}
              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              onClick={e => { e.stopPropagation(); setMenuOpen(o => !o) }}>
              <MoreHorizontal size={14} className="text-ink-faint" />
            </button>
          )}
          {unassigned(deal)
            ? <span className="k-deal-owner k-deal-owner--none" title="Unassigned" aria-hidden="true" />
            : <OwnerChip owner={deal.owner} />}
        </div>
      </div>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={e => { e.stopPropagation(); setMenuOpen(false) }} />
          <div className="absolute right-2 top-8 z-20 bg-surface-card border border-edge-control rounded-control shadow-deal-hover py-1 min-w-[150px]">
            {onLogActivity && (
              <button className="w-full text-left px-3 py-2 text-12 text-ink-body hover:bg-surface-row-hover transition-colors"
                onClick={e => { e.stopPropagation(); setMenuOpen(false); onLogActivity(deal) }}>
                Log activity
              </button>
            )}
            <button className="w-full text-left px-3 py-2 text-12 text-ink-body hover:bg-surface-row-hover transition-colors"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onCloseAs?.(deal, 'won') }}>
              Close as won…
            </button>
            <button className="w-full text-left px-3 py-2 text-12 text-ink-body hover:bg-surface-row-hover transition-colors"
              onClick={e => { e.stopPropagation(); setMenuOpen(false); onCloseAs?.(deal, 'lost') }}>
              Close as lost…
            </button>
          </div>
        </>
      )}
      <div className="k-deal-amountrow">
        <span className="k-deal-amount text-ink-primary">{fmtCompact(deal.amount, deal.currency)}</span>
        <span className={`k-deal-age${ageStale(deal) ? ' k-deal-age--stale' : ''}`}>{daysLabel(deal.daysInStage)}</span>
      </div>
      {/* Ruled footer: date + the honest right-slot occupant. PIPELINE.md's
          per-deal status capsule has no backing field (omitted, not faked) —
          closed cards carry the outcome badge; open cards carry the SERVER's
          worst health signal (2.4, P2-12: at most one badge, "+N" for the
          rest), which REPLACED the card's own "> 5 days" rule so board and
          Detail can never disagree; a healthy open card carries nothing. */}
      <div className="k-deal-foot">
        {closed ? (
          <>
            <span className="k-deal-date"><Calendar size={13} aria-hidden="true" />{deal.closedDate ? `Closed ${deal.closedDate}` : ''}</span>
            <span className="k-badge"
              style={lost ? { background: danger.bg, color: danger.text } : { background: success.bg, color: success.text }}>
              <span className="k-badge-dot" />{lost ? 'LOST' : 'WON'}
            </span>
          </>
        ) : (
          <>
            {/* Slice 4: the boards' calendar footer — the date, OVERDUE by the
                server's verdict (closeState), "No close date" when the wire
                has none. */}
            <span className={`k-deal-date k-deal-date--${closeState(deal)}`}>
              <Calendar size={13} aria-hidden="true" />
              {closeState(deal) === 'missing' ? 'No close date' : closeState(deal) === 'overdue' ? `Overdue · ${deal.closeDate}` : deal.closeDate}
            </span>
            {badge && <DealHealthBadge signal={badge.signal} more={badge.more} upper />}
          </>
        )}
      </div>
    </article>
  )
}

// ── The board ─────────────────────────────────────────────────────────────────

export function SalesPipeline({ onSelectDeal, newDealOpen = false, onNewDealClose }: {
  onSelectDeal?: (id: string) => void
  /** Slice 4: the New Deal drawer's open state when opened from the shell's
   *  primary-action slot (Dashboard.tsx holds it); the per-lane "+" keeps its
   *  own preset state below. */
  newDealOpen?: boolean
  onNewDealClose?: () => void
}) {
  const [search, setSearch] = useState('')
  // Slice 4: the boards' chips — they filter and sort the rows the board displays.
  const [owner, setOwner] = useState('')
  const [close, setClose] = useState<CloseFilter>('any')
  const [sort, setSort] = useState<SortKey>('value')
  const [attentionOnly, setAttentionOnly] = useState(false)
  // The Close-date chip's "now", read once per mount (a render must not call the clock).
  const [nowMs] = useState(() => Date.now())
  const { data, loading, error } = useAsync(fetchDeals, [])
  // Header figures: the server's KPI formulas over the FULL deal set (see
  // header comment), from the read shared with the sidebar's count. A half
  // that has never landed renders a shorter summary row, never a fabricated
  // figure — .k-summary is a flex list precisely so it can. The all-time won
  // pair is the server's closed_won stage total (/v1/sales/stats, slice 4) —
  // the board's fourth figure, never a sum over the list.
  const salesFigures = useSalesFigures()
  // The board arriving (first mount, or back from Deal Detail) reads once —
  // joining the shell's read when both ask in the same commit.
  useEffect(() => { void loadSalesFigures() }, [])
  // Deals created from the drawer, newest first, MERGED with the fetched list
  // at render (derived, not synced into a copy) — a created deal lands in its
  // stage column and every derived figure (column counts/totals) recomputes
  // from the merged list.
  const [createdDeals, setCreatedDeals] = useState<SalesDeal[]>([])
  // Slice 3 (F7): the ids of cards created in this visit that have not yet
  // finished their enter animation.
  const [entering, setEntering] = useState<ReadonlySet<string>>(() => new Set())
  // Mutation responses (stage moves, closes) override fetched/created deals
  // at render — the same derive-don't-sync doctrine as createdDeals (§5.B.1).
  const [patched, setPatched] = useState<Record<string, SalesDeal>>({})
  // Doubles as the drawer's open flag and its preset stage (null = closed).
  const [newDealStage, setNewDealStage] = useState<DealStage | null>(null)
  // The close ceremony (a closed-column drop, or a card kebab item).
  const [closing, setClosing] = useState<{ deal: SalesDeal; outcome: CloseOutcome } | null>(null)
  // Drag state: the id travels in a ref (no re-render mid-drag); the hovered
  // column is state because it paints the drop highlight.
  const draggingId = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<DealStage | null>(null)

  // Log Activity slice C1. R5: the card's entry — ONE deal read (mapped as
  // the board maps it, plus its contact id) and, when the deal names one, ONE
  // contact read; then the sheet opens with both. Focus comes back to the
  // card's kebab, else the card itself (the host's own fallback after that).
  // R6: once an activity naming a deal exists, that ONE deal is re-read into
  // the patched map, so its health badge reads the server's new verdict.
  const logActivity = useLogActivity()
  const [logOpening, setLogOpening] = useState(false)
  useEffect(() => logActivity?.subscribe(created => {
    const id = created.dealId
    if (!id) return
    fetchDealCard(id)
      .then(r => setPatched(p => ({ ...p, [id]: r.deal })))
      .catch(() => { /* the card keeps its last read; the next load corrects it */ })
  }), [logActivity])
  async function logFromCard(deal: SalesDeal) {
    if (!logActivity || logOpening) return
    const back = () =>
      document.querySelector<HTMLElement>(`[data-deal-card="${deal.id}"] button[aria-label^="Deal options for"]`) ??
      document.querySelector<HTMLElement>(`[data-deal-card="${deal.id}"]`)
    setLogOpening(true)
    try {
      const { deal: card, contactId } = await fetchDealCard(deal.id)
      const contact = contactId ? await fetchContact(contactId) : null
      logActivity.open({ deal: card, contact }, back)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not open this deal to log an activity'))
      back()?.focus()
    } finally {
      setLogOpening(false)
    }
  }

  // The viewportOwning main is unpadded — transitional states pad themselves.
  if (loading) return <div className="p-6 h-full overflow-y-auto"><LoadingState /></div>
  if (error) return <div className="p-6"><ErrorState message={error} /></div>

  const allDeals = [...createdDeals, ...(data ?? [])].map(d => patched[d.id] ?? d)

  // One move path for drops and keyboard moves: open target = optimistic
  // stage write + revert on error; closed target = the close ceremony.
  async function moveTo(deal: SalesDeal, target: DealStage) {
    const action = dropAction(deal.stage, target)
    if (action.kind === 'none') return
    if (action.kind === 'close') { setClosing({ deal, outcome: action.outcome }); return }
    const slug = STAGE_TO_SLUG[target]
    if (!slug) return
    // Optimistic move + revert on error (the frozen MC board's shape).
    const id = deal.id
    const prev = patched[id]
    setPatched(p => ({ ...p, [id]: { ...deal, stage: target } }))
    try {
      const wire = await moveDealStage(id, slug)
      setPatched(p => ({ ...p, [id]: mapDeal(wire) }))
      toast.success(`Moved to ${target}`)
    } catch (e) {
      setPatched(p => {
        const next = { ...p }
        if (prev) next[id] = prev
        else delete next[id]
        return next
      })
      toast.error(apiErrorMessage(e, 'Failed to move deal'))
    }
  }

  async function handleDrop(target: DealStage) {
    const id = draggingId.current
    draggingId.current = null
    setDragOver(null)
    if (!id) return
    const deal = allDeals.find(d => d.id === id)
    if (!deal) return
    await moveTo(deal, target)
  }

  const filtered = applyBoardFilters(allDeals, { search, owner, close, attentionOnly, nowMs })
  const owners = ownerOptions(allDeals)
  const attention = attentionCount(allDeals)

  // Lane figures describe what the board displays (pipelineView.laneFigures):
  // value per lane over the GBP-COMPARABLE leg (0111 — original-currency
  // pence are not summable; an uncaptured row drops out), an open lane's
  // share of the OPEN total and its bar against the largest open lane; the
  // closed lanes read all-time.
  const figures = laneFigures(filtered, STAGES)

  const head = headFigures(salesFigures.reports, salesFigures.stats)
  const r = salesFigures.reports

  return (
    <div className="k-pipeline k-pipeline--calm page-enter">
      {/* ── Title row: the boards' four figures | the page's own search ── */}
      <div className="k-pipeline-head">
        <div className="flex flex-col gap-[10px]">
          <h1 className="k-pipeline-title text-ink-primary">Pipeline</h1>
          <div className="k-summary" aria-label="Pipeline figures">
            {r && <>
              <SummaryItem value={fmtCompact(head.openPence)} label="open" />
              <SummaryItem value={String(head.openCount)} label={head.openCount === 1 ? 'deal' : 'deals'} />
              <SummaryItem value={head.avgPence != null ? fmtCompact(head.avgPence) : '—'} label="average" />
            </>}
            {head.won && <SummaryItem value={fmtCompact(head.won.pence)} label={`won · ${head.won.count} ${head.won.count === 1 ? 'deal' : 'deals'}${head.won.excluded ? ' ' + head.won.excluded : ''}`} />}
          </div>
        </div>
        {/* The boards put the deal search in the bar's capsule; 2b (5) ruled the
            capsule out of the shell, so the page keeps its own search here. The
            Board / List toggle is OMITTED (no list view exists); New Deal is the
            shell's slot button (Dashboard.tsx). */}
        <div className="k-search k-search--compact">
          <Search size={13} className="flex-shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deals…"
            className="flex-1 min-w-0 bg-transparent border-0 p-0 text-ink-body placeholder:text-ink-faint"
            style={{ fontSize: 'inherit' }}
          />
        </div>
      </div>

      {/* ── Filters: the boards' three chips + the attention chip ── */}
      <div className="k-pipeline-filters">
        <label className="k-chip"><span className="k-chip-key">Owner</span>
          <select aria-label="Owner" value={owner} onChange={e => setOwner(e.target.value)}>
            <option value="">Anyone</option>
            <option value="—">Unassigned</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="k-chip"><span className="k-chip-key">Close date</span>
          <select aria-label="Close date" value={close} onChange={e => setClose(e.target.value as CloseFilter)}>
            {CLOSE_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="k-chip"><span className="k-chip-key">Sort</span>
          <select aria-label="Sort" value={sort} onChange={e => setSort(e.target.value as SortKey)}>
            {SORT_KEYS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <span className="flex-1" />
        {attention > 0 && (
          <button type="button" className="k-chip k-chip--attention" aria-pressed={attentionOnly} onClick={() => setAttentionOnly(v => !v)}>
            <AlertCircle size={14} aria-hidden="true" />{attentionLabel(attention)}
          </button>
        )}
      </div>

      {/* ── Board: lanes scroll vertically, the board horizontally ── */}
      <div className="k-board">
        {STAGES.map(stage => {
          const stageDeals = sortDeals(filtered.filter(d => d.stage === stage), sort)
          const fig = figures.get(stage) ?? { count: 0, valuePence: 0, sharePct: 0, barPct: 0 }
          const open = OPEN_STAGES.includes(stage)
          const won = stage === 'Closed Won', lost = stage === 'Closed Lost'
          return (
            <section key={stage} className={`k-stage${won ? ' k-stage--won' : lost ? ' k-stage--lost' : ''}`}
              aria-label={`${stage} stage`}
              onDragOver={e => { e.preventDefault(); if (dragOver !== stage) setDragOver(stage) }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(cur => (cur === stage ? null : cur)) }}
              onDrop={e => { e.preventDefault(); void handleDrop(stage) }}>
              <div className="k-stage-head">
                <div className="k-stage-titlerow">
                  <div className="k-stage-name">
                    <span className="k-stage-dot" style={{ background: STAGE_DOT[stage] }} />
                    <h2 className="k-stage-label k-truncate text-ink-primary">{laneLabel(stage)}</h2>
                    <span className="k-stage-count">{stageDeals.length}</span>
                  </div>
                  {/* Open lanes' + preset the create drawer to their stage; the
                      closed lanes keep the stub — a deal is never born closed
                      (the close ceremony's business, not create's). */}
                  <button className="k-stage-add" aria-label={`Add deal to ${stage}`}
                    onClick={() => open ? setNewDealStage(stage) : toast.info('Add deal coming soon')}>
                    +
                  </button>
                </div>
                <div className="k-stage-metrics">
                  <span className="k-stage-value text-ink-primary">{fmtCompact(fig.valuePence)}</span>
                  <span className={`k-stage-share${won ? ' k-stage-share--won' : ''}`}>{shareLabel(stage, fig.sharePct)}</span>
                </div>
                <div className="k-stage-bar">
                  <div style={{ width: `${fig.barPct}%`, background: STAGE_DOT[stage] }} />
                </div>
              </div>

              <div className="k-lane" data-dropactive={dragOver === stage || undefined}>
                {stageDeals.length === 0 ? (
                  <div className="k-lane-empty">
                    {search || owner || close !== 'any' || attentionOnly ? 'No matching deals' : 'No deals in this stage'}
                    {open && !search && !owner && close === 'any' && !attentionOnly && (
                      <button onClick={() => setNewDealStage(stage)}>+ ADD DEAL</button>
                    )}
                  </div>
                ) : (
                  stageDeals.map(deal => (
                    <DealCard key={deal.id} deal={deal} onOpen={onSelectDeal}
                      entering={entering.has(deal.id)}
                      onEntered={id => setEntering(prev => { const next = new Set(prev); next.delete(id); return next })}
                      onDragStart={id => { draggingId.current = id }}
                      onDragEnd={() => { draggingId.current = null; setDragOver(null) }}
                      onCloseAs={(d, outcome) => setClosing({ deal: d, outcome })}
                      onLogActivity={logActivity ? d => void logFromCard(d) : undefined}
                      onMoveKey={(d, dir) => {
                        const idx = STAGES.indexOf(d.stage)
                        const target = STAGES[idx + dir]
                        if (target) void moveTo(d, target)
                      }} />
                  ))
                )}
              </div>
            </section>
          )
        })}
      </div>

      {/* Drawer */}
      {(newDealOpen || newDealStage) && (
        <NewDealDrawer
          initialStage={newDealStage ?? 'New Lead'}
          onClose={() => { setNewDealStage(null); onNewDealClose?.() }}
          onCreated={deal => {
            setCreatedDeals(prev => [deal, ...prev])
            setEntering(prev => new Set(prev).add(deal.id))
          }}
          onOpenDeal={onSelectDeal}
        />
      )}

      {/* Close ceremony (§5.C) — the response is the updated wire deal;
          patching it in re-derives every column count/total. */}
      {closing && (
        <CloseDealModal
          deal={{ id: closing.deal.id, title: closing.deal.name, company: closing.deal.company, amountPence: closing.deal.amount, currency: closing.deal.currency }}
          outcome={closing.outcome}
          onCancel={() => setClosing(null)}
          onClosed={wire => {
            setPatched(p => ({ ...p, [wire.id]: mapDeal(wire) }))
            setClosing(null)
          }}
        />
      )}
    </div>
  )
}
